import { IResourceList } from 'src/components/challenge/ChallengeInterfaces';
import {
  CampaignModuleStatusEnum,
  ResourceTypeDetailEnum,
  ResourceTypeEnum,
} from 'src/shared/enums';
import {
  IChallengeCheckout,
  IChallengeCheckoutProduct,
} from 'src/shared/models';

const {
  CHECKOUT_DESCRIPTION,
  CHECKOUT_QUESTION,
  CHECKOUT_ADDRESS_TYPE,
  CHECKOUT_BUTTON_NAME,
  CHECKOUT_COMPLETED_LABEL,
  CHECKOUT_PRODUCT_EAN,
  CHECKOUT_PRODUCT_IMAGE,
  CHECKOUT_PRODUCT_CURRENT_STOCK,
  CHECKOUT_PRODUCT_ORIGINAL_STOCK,
  CHECKOUT_IMAGE_MOBILE,
  CHECKOUT_IMAGE_DESKTOP,
  CHECKOUT_END_MESSAGE,
  CHECKOUT_SHIPPING_TYPE_QUESTION,
  CHECKOUT_TERMS_URL,
  CHECKOUT_TERMS_LABEL,
  CHECKOUT_PRODUCT_ITEM_DESCRIPTION,
} = ResourceTypeDetailEnum;

const { CHECKOUT_PRODUCT, CHECKOUT_SUPPLEMENTARY_PRODUCT } = ResourceTypeEnum;

const getCompletedCheckoutInfo = (resource: IResourceList) => {
  let completeCheckoutInfo: {
    sapCustomerId: string | null;
    qrUrl: string | null;
    idSelectedProduct: number | null;
    checkoutId: number | null;
  } = {
    sapCustomerId: null,
    qrUrl: null,
    idSelectedProduct: null,
    checkoutId: null,
  };

  const userResource = resource.usersM2MList.find(
    ({ status, statusResource }) => {
      return status && statusResource === CampaignModuleStatusEnum.GENERIC_DONE;
    }
  );

  if (userResource?.value) {
    const extraData = JSON.parse(userResource.value);
    completeCheckoutInfo = {
      qrUrl: extraData?.checkout?.qr || null,
      sapCustomerId: extraData?.checkout?.S_CODDROPPOINT || null,
      idSelectedProduct: extraData?.checkout?.idResource || null,
      checkoutId: extraData?.checkout?.BSTKD || null,
    };
  }

  return completeCheckoutInfo;
};

const getCheckoutProduct = ({
  resourceDetailList,
  name: lead,
  idResource,
  score,
  idResourceType,
}: IResourceList): IChallengeCheckoutProduct => {
  let product: IChallengeCheckoutProduct;
  const productScore = score?.points || 0;

  resourceDetailList
    .filter(({ status }) => status)
    .forEach(({ idResourceTypeD: { idResourceTypeD }, value }) => {
      if (idResourceType.idResourceType === CHECKOUT_PRODUCT) {
        switch (idResourceTypeD) {
          case CHECKOUT_PRODUCT_EAN:
            product = {
              ...product,
              id: value,
            };
            break;
          case CHECKOUT_PRODUCT_IMAGE:
            product = {
              ...product,
              img: value,
            };
            break;
          case CHECKOUT_PRODUCT_CURRENT_STOCK:
            product = {
              ...product,
              stock: +value || 0,
            };
            break;
          case CHECKOUT_PRODUCT_ITEM_DESCRIPTION:
            product = {
              ...product,
              description: value,
            };
            break;
        }
      }
      if (idResourceType.idResourceType === CHECKOUT_SUPPLEMENTARY_PRODUCT) {
        switch (idResourceTypeD) {
          case CHECKOUT_PRODUCT_EAN:
            product = {
              ...product,
              id: value,
              resourceType: idResourceType.idResourceType,
            };
            break;
          case CHECKOUT_PRODUCT_ORIGINAL_STOCK:
            product = {
              ...product,
              stock: +value || 0,
            };
            break;
        }
      }
    });
  return { ...product, lead, idResource, score: productScore };
};

export const buildChallengeCheckout = (
  resource: IResourceList
): IChallengeCheckout => {
  const {
    resourceDetailList,
    name: title,
    idResource,
    resourceList,
    score,
  } = resource;
  let item: IChallengeCheckout;
  let points = score?.points || 0;

  resourceDetailList
    .filter(({ status }) => status)
    .forEach(({ idResourceTypeD: { idResourceTypeD }, value }) => {
      switch (idResourceTypeD) {
        case CHECKOUT_DESCRIPTION:
          item = {
            ...item,
            checkoutName: value,
          };
          break;
        case CHECKOUT_QUESTION:
          item = {
            ...item,
            lead: value,
          };
          break;
        case CHECKOUT_ADDRESS_TYPE:
          item = {
            ...item,
            addressType: value,
          };
          break;
        case CHECKOUT_BUTTON_NAME:
          item = {
            ...item,
            buttonName: value,
          };
          break;
        case CHECKOUT_COMPLETED_LABEL:
          item = {
            ...item,
            completedLabel: value,
          };
          break;
        case CHECKOUT_IMAGE_MOBILE:
          item = {
            ...item,
            imageMobile: value,
          };
          break;
        case CHECKOUT_IMAGE_DESKTOP:
          item = {
            ...item,
            imageDesktop: value,
          };
          break;
        case CHECKOUT_END_MESSAGE:
          item = {
            ...item,
            checkoutDoneMessage: value,
          };
          break;
        case CHECKOUT_SHIPPING_TYPE_QUESTION:
          item = { ...item, shippingQuestion: value };
          break;
        case CHECKOUT_TERMS_LABEL:
          item = { ...item, termsLabel: value };
          break;
        case CHECKOUT_TERMS_URL:
          item = { ...item, termsUrl: value };
          break;
      }
    });

  const arrProducts: IChallengeCheckoutProduct[] =
    resourceList
      ?.filter(
        ({ idResourceType: { idResourceType } }) =>
          ![
            ResourceTypeEnum.EMAIL_WITH_DELAY,
            ResourceTypeEnum.EMAIL_WITH_DATE,
          ].includes(idResourceType)
      )
      ?.map((resource) => getCheckoutProduct(resource)) || [];
  const maxProductScore = getMaxProductPoints(arrProducts);
  points += maxProductScore;

  const completeCheckoutInfo = getCompletedCheckoutInfo(resource);
  return {
    ...item,
    title,
    idResource,
    arrProducts,
    ...completeCheckoutInfo,
    points,
  };
};

const getMaxProductPoints = (arrProducts: IChallengeCheckoutProduct[]) => {
  if (!arrProducts?.length) return 0;

  const maxScoreProduct = arrProducts.reduce((prev, current) => {
    if (
      current.resourceType === ResourceTypeEnum.CHECKOUT_SUPPLEMENTARY_PRODUCT
    ) {
      return prev;
    } else {
      return prev.score > current.score ? prev : current;
    }
  });

  return maxScoreProduct?.score || 0;
};
